import { isEmpty, isEqual } from "lodash"
import { CreateRoleDto } from "../../domain/roles/roles"
import { isValidEmail } from "../Users/usersModel"

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const laborSkuSettingLabels = {
  MaintenanceLaborSku: 'Maintenance Labor SKU',
  ProjectLaborSku: 'Project Labor SKU',
  ServiceCallSku: 'Service Call Fee SKU'
} as const
export type TenantForSidebarDto = {
  id: number
  name: string
  path: string
  active: boolean
}

export type TenantDTO = {
  id: number
  name: string
  active: boolean
  email: string
  phoneNumber: string
  reviewWebsiteAddress: string
  address: string
  maintenanceLaborSKU: string
  projectLaborSKU: string
  serviceCallSKU: string
  snowDivisionsGroupName: string
  hasAdminAccount: boolean
  hasLunchBreaks: boolean
  defaultLunchBreak?: number
  projectInvoiceLineItemType: number
  serviceInvoiceLineItemType: number
  maintenanceInvoiceLineItemType: number
  enableCreditCardPayments: boolean
  enableACHPayments: boolean
  path: string
  timeZoneId: string
  defaultQboSalesTaxId?: number
  defaultQboExemptSalesTaxId?: number
  showSuccessChecklist: boolean
  allowForemenToSeeOtherForemen: boolean
  planName: string
  userCount?: number
  estimateLimit?: number | null
  stripeSubscriptionId?: string
  customerDisplayAsDefault: number
  enableLaborAdjustedCost: boolean
  closeUnbillableGaps: boolean
  requireClockOutNotes: boolean
  requireClockOutPhoto: boolean
  accountConfigCompleted: boolean
}

export type TenantUpdateDTO = {
  tenant: TenantDTO
  adminAccount?: TenantAccountDTO
  images: TenantUpdateImagesDTO
}

export type TenantFormState = TenantUpdateDTO & {
  roles: CreateRoleDto[]
  serverImages: TenantImageUrls,
  isAdminAccountEmailAvailable: boolean
}

export type TenantAccountDTO = {
  email?: string
  password?: string
  displayName?: string
}

export type TenantUpdateImagesDTO = {
  logo?: string
  customer?: string
  property?: string
  projects?: string
  item?: string
  template?: string
  setup?: string
}

export type TenantImageUrls = {
  logo: string
  customer: string
  property: string
  projects: string
  item: string
  template: string
  setup: string
}

export const emptyTenantState = (images: TenantImageUrls): TenantFormState => ({
  tenant: emptyTenant(),
  adminAccount: {},
  images: {},
  serverImages: images,
  roles: [],
  isAdminAccountEmailAvailable: false,
})

export const emptyTenant = (): TenantDTO => ({
  id: 0,
  name: '',
  active: true,
  email: '',
  phoneNumber: '',
  reviewWebsiteAddress: '',
  address: '',
  maintenanceLaborSKU: '',
  projectLaborSKU: '',
  serviceCallSKU: '',
  snowDivisionsGroupName: '',
  hasAdminAccount: false,
  hasLunchBreaks: false,
  defaultLunchBreak: 0,
  projectInvoiceLineItemType: 0,
  serviceInvoiceLineItemType: 0,
  maintenanceInvoiceLineItemType: 0,
  enableCreditCardPayments: false,
  enableACHPayments: false,
  path: '',
  timeZoneId: '',
  defaultQboSalesTaxId: 0,
  defaultQboExemptSalesTaxId: 0,
  showSuccessChecklist: true,
  allowForemenToSeeOtherForemen: false,
  planName: '',
  userCount: 0,
  estimateLimit: null,
  stripeSubscriptionId: '',
  customerDisplayAsDefault: 0,
  enableLaborAdjustedCost: false,
  closeUnbillableGaps: false,
  requireClockOutNotes: false,
  requireClockOutPhoto: false,
  accountConfigCompleted: false,
})

export const emptyServerTenantImages = (): TenantImageUrls => {
  return {
    logo: '',
    customer: '',
    property: '',
    projects: '',
    item: '',
    template: '',
    setup: '',
  }
}

export const hasTenantChanged = (serverState: TenantDTO, tenantFormState: TenantFormState) => {
  const { tenant: currentState, roles, images } = tenantFormState

  const hasImagesChanged = !isEmpty(images)

  const stateChanged = !isEqual(serverState, currentState)
  const rolesChanged = hasRolesChanged(serverState, currentState, roles)

  return stateChanged || rolesChanged || hasImagesChanged
}

export const hasRolesChanged = (serverState: TenantDTO, currentState: TenantDTO, roles: CreateRoleDto[]) => {
  if (roles.length === 1 && roles[0].removed && isEqual(serverState, currentState)) {
    return false
  }

  const allRolesRemoved = roles.length > 0 && roles.every(role => role.removed)

  if (allRolesRemoved && isEqual(serverState, currentState)) {
    return false
  }

  const allRolesHaveNameAndDescription = roles
    .filter(r => !r.removed)
    .every(role => role.rolename.length > 0 && role.description.length > 0)

  if (!allRolesHaveNameAndDescription) {
    return false
  }

  const rolesChanged = roles.some(role => !role.removed && role.rolename.length > 0 && role.description.length > 0)
  return rolesChanged
}

const buildTenantFolderPath = (bucketName: string, tenantPath: string | undefined) => {
  return `https://${bucketName}.s3.amazonaws.com/${tenantPath}`
}

export const generateServerTenantImages = (bucketName: string | undefined, tenantImagesPath: string): TenantImageUrls => {
  return {
    customer: `${bucketName ? buildTenantFolderPath(bucketName, tenantImagesPath) : ''}/images/default-customer.jpg`,
    item: `${bucketName ? buildTenantFolderPath(bucketName, tenantImagesPath) : ''}/images/default-item.jpg`,
    logo: `${bucketName ? buildTenantFolderPath(bucketName, tenantImagesPath) : ''}/images/logo.png`,
    projects: `${bucketName ? buildTenantFolderPath(bucketName, tenantImagesPath) : ''}/images/default-projects.jpg`,
    property: `${bucketName ? buildTenantFolderPath(bucketName, tenantImagesPath) : ''}/images/default-property.jpg`,
    template: `${bucketName ? buildTenantFolderPath(bucketName, tenantImagesPath) : ''}/images/default-template.jpg`,
    setup: `${bucketName ? buildTenantFolderPath(bucketName, tenantImagesPath) : ''}/images/default-setup.jpg`
  }
}

export const areAllFieldsValid = (tenantFormState: TenantFormState, isQboUSAAccount: boolean): boolean => {
  const { tenant, adminAccount, isAdminAccountEmailAvailable } = tenantFormState
  const { defaultQboSalesTaxId, defaultQboExemptSalesTaxId } = tenant

  const hasTenantRequiredFields = checkIfTenantIsValid(tenant)

  const hasRequiredSalesTaxFields = isQboUSAAccount
    || (defaultQboSalesTaxId !== 0 && defaultQboExemptSalesTaxId !== 0)

  const hasAdminAccountRequiredFields = validateTenantAdminSection(
    tenant.hasAdminAccount,
    adminAccount?.email ?? '',
    adminAccount?.password ?? '',
    isAdminAccountEmailAvailable
  )

  if (!hasAdminAccountRequiredFields) {
    return false
  }

  return hasTenantRequiredFields && hasRequiredSalesTaxFields
}

export const checkIfTenantIsValid = (tenant: TenantDTO): boolean => {
  return !!tenant.name && !!tenant.email && !!tenant.email.match(EMAIL_REGEX) && !!tenant.phoneNumber && !!tenant.planName
    && (!planHasEstimateLimit(tenant.planName) || isEstimateLimitValid(tenant.estimateLimit))
}

const PLANS_WITH_ESTIMATE_LIMIT = ['FREE_STARTER_PLAN']

export const planHasEstimateLimit = (planName: string): boolean => PLANS_WITH_ESTIMATE_LIMIT.includes(planName)

export const toTenantForSave = (tenant: TenantDTO): TenantDTO => ({
  ...tenant,
  estimateLimit: planHasEstimateLimit(tenant.planName) ? tenant.estimateLimit : null
})

export const parseEstimateLimitInput = (value: string): number | null => {
  if (value.trim() === '') {
    return null
  }
  return Number.parseInt(value)
}

export const isEstimateLimitValid = (estimateLimit?: number | null): boolean => {
  if (estimateLimit === null || estimateLimit === undefined) {
    return true
  }
  return Number.isInteger(estimateLimit) && estimateLimit >= 0
}

export const generatePasswordStrengthMessage = (password: string): string => {
  if (!password) {
    return 'Password is required'
  }

  if (password.length < 8) {
    return 'Password must be at least 8 characters long'
  }

  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter'
  }

  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter'
  }

  if (!/[^a-zA-Z0-9]/.test(password)) {
    return 'Password must contain at least one special character'
  }

  return ''
}

export const checkIfValidPassword = (password: string): boolean => {
  if (generatePasswordStrengthMessage(password) === '') {
    return true
  }

  return false
}

export const validateTenantAdminSection = (hasAdminAccount: boolean, email: string, password: string, isEmailAvailable: boolean): boolean => {
  if (!hasAdminAccount && !email && !password) {
    return true
  }

  if (!hasAdminAccount && (email || password)) {
    const emailValid = email && isValidEmail(email) && isEmailAvailable
    const passwordValid = checkIfValidPassword(password ?? '')

    if (emailValid && passwordValid) {
      return true
    }
  }

  return hasAdminAccount
}